import React, { useState, useCallback } from 'react';
import { AppButton, AppTextField, AppForm, AppFormLabel } from '@/components/atoms';
import { CollapsibleCard } from '@/components/molecules';
import { AddressSearchModal } from '@/components/organisms';
import { type AddressResult } from '@/hooks/useAddressSearch';
// useAddressHelper covers split postal_code1/2; this component uses a combined 7-digit field, so we keep inline logic below.
import type { PractitionerFormData } from '@/types/documentTemplate.types';

export type PractitionerFieldErrors = Partial<Record<keyof PractitionerFormData, string>>;

interface DocumentTemplatePractitionerSectionProps {
  value: PractitionerFormData;
  onChange: (field: keyof PractitionerFormData, value: string) => void;
  onSubmit: () => void;
  errors?: PractitionerFieldErrors;
  submitting?: boolean;
  disabled?: boolean;
}

const DocumentTemplatePractitionerSection: React.FC<DocumentTemplatePractitionerSectionProps> = ({
  value,
  onChange,
  onSubmit,
  errors = {},
  submitting = false,
  disabled = false,
}) => {
  const [addressLookupOpen, setAddressLookupOpen] = useState(false);

  // Parse combined postal code – allow any chars (user can type letters too), just split for modal
  const pc = (value.practitioner_postal_code || '').trim();
  const pc1 = pc.substring(0, 3);
  const pc2 = pc.substring(3, 7);

  // Handle address lookup button click using default modal behavior
  const handleAddressLookupOpen = useCallback(() => {
    setAddressLookupOpen(true);
  }, []);

  // Handle address selection from modal
  const handleAddressSelect = (result: AddressResult) => {
    const postalCode = result.postal_code1 + result.postal_code2;
    onChange('practitioner_postal_code', postalCode);
    onChange('practitioner_address', result.address);
    setAddressLookupOpen(false);
  };

  return (
    <>
      <CollapsibleCard title="施術所情報" className="mb-4">
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2 xl:grid-cols-3">
            <AppForm.Item
              label={<AppFormLabel label="施術所名" />}
              validateStatus={errors.practitioner_name ? 'error' : ''}
              help={errors.practitioner_name}
              layout="vertical"
              className="mb-0 min-w-0"
            >
              <AppTextField
                value={value.practitioner_name}
                onChange={e => onChange('practitioner_name', e.target.value)}
                className="w-full"
                placeholder="入力してください"
                maxLength={255}
                disabled={disabled}
              />
            </AppForm.Item>

            <AppForm.Item
              label={<AppFormLabel label="代表者名" />}
              validateStatus={errors.representative_name ? 'error' : ''}
              help={errors.representative_name}
              layout="vertical"
              className="mb-0 min-w-0"
            >
              <AppTextField
                value={value.representative_name}
                onChange={e => onChange('representative_name', e.target.value)}
                className="w-full"
                placeholder="入力してください"
                maxLength={255}
                disabled={disabled}
              />
            </AppForm.Item>

            <AppForm.Item
              label={<AppFormLabel label="電話番号" />}
              validateStatus={errors.practitioner_telephone ? 'error' : ''}
              help={errors.practitioner_telephone}
              layout="vertical"
              className="mb-0 min-w-0 sm:col-span-2 xl:col-span-1"
            >
              <AppTextField
                value={value.practitioner_telephone}
                onChange={e =>
                  onChange('practitioner_telephone', e.target.value.replace(/[^0-9-]/g, ''))
                }
                className="w-full"
                placeholder="入力してください"
                maxLength={13}
                disabled={disabled}
              />
            </AppForm.Item>
          </div>

          <div className="grid grid-cols-1 gap-x-4 gap-y-3 lg:grid-cols-[minmax(0,220px)_auto_minmax(0,1fr)] lg:items-start">
            <AppForm.Item
              label={<AppFormLabel label="郵便番号" />}
              validateStatus={errors.practitioner_postal_code ? 'error' : ''}
              help={errors.practitioner_postal_code}
              layout="vertical"
              className="mb-0 min-w-0"
            >
              <AppTextField
                value={value.practitioner_postal_code}
                onChange={e => onChange('practitioner_postal_code', e.target.value.slice(0, 7))}
                className="w-full"
                placeholder="ハイフンなしで入力してください"
                maxLength={7}
                disabled={disabled}
              />
            </AppForm.Item>

            <div className="flex items-start lg:pt-[35px]">
              <AppButton
                type="primary"
                onClick={handleAddressLookupOpen}
                className="w-full px-3 py-2 text-sm font-medium sm:w-[100px]"
                disabled={disabled}
              >
                住所補助
              </AppButton>
            </div>

            <AppForm.Item
              label={<AppFormLabel label="住所" />}
              validateStatus={errors.practitioner_address ? 'error' : ''}
              help={errors.practitioner_address}
              layout="vertical"
              className="mb-0 min-w-0"
            >
              <AppTextField
                value={value.practitioner_address}
                onChange={e => onChange('practitioner_address', e.target.value)}
                className="w-full"
                placeholder="入力してください"
                maxLength={255}
                disabled={disabled}
              />
            </AppForm.Item>
          </div>

          <div className="flex flex-col pt-2 sm:flex-row sm:justify-end">
            <AppButton
              type="primary"
              onClick={onSubmit}
              disabled={disabled || submitting}
              className="w-full px-6 py-2 font-medium disabled:cursor-not-allowed sm:w-auto"
            >
              {submitting ? '更新中...' : '更新'}
            </AppButton>
          </div>
        </div>
      </CollapsibleCard>

      <AddressSearchModal
        isOpen={addressLookupOpen}
        onClose={() => setAddressLookupOpen(false)}
        onSelect={handleAddressSelect}
        initialPostalCode1={pc1}
        initialPostalCode2={pc2}
        initialAddress={value.practitioner_address ?? ''}
        addressType="patient"
        title="住所補助"
      />
    </>
  );
};

export default DocumentTemplatePractitionerSection;
